import { useEffect, useState } from 'react';
import { Shell } from '../components/Layout.js';
import { api } from '../api/client.js';
import { useAuth } from '../state/auth.js';
import { useDocumentMeta } from '../lib/document-meta.js';
import { useT } from '../state/i18n.js';
import type { SiteAnalyticsResponse } from '@cloudbtl/shared';

export function AnalyticsPage(): JSX.Element {
  const t = useT();
  useDocumentMeta({ title: t({ ko: '제품 분석', en: 'Product analytics' }), noindex: true });
  const auth = useAuth();
  const [days, setDays] = useState(14);
  const [data, setData] = useState<SiteAnalyticsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!auth.user?.isAdmin) return;
    api
      .siteAnalytics(days)
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'));
  }, [auth.user, days]);

  if (!auth.user) {
    return (
      <Shell>
        <section className="analytics">
          <p>{t({ ko: '제품 분석을 보려면 로그인하세요.', en: 'Sign in to view product analytics.' })}</p>
        </section>
      </Shell>
    );
  }

  // 전사 제품 분석은 내부 관리자 전용. 서버가 최종 게이트(403)지만 화면도 명확히 막는다.
  if (!auth.user.isAdmin) {
    return (
      <Shell>
        <section className="analytics">
          <h1>{t({ ko: '내부 전용', en: 'Internal only' })}</h1>
          <p>
            {t({
              ko: '이 페이지는 CloudBTL 내부 제품 분석입니다. 내 문서의 열람 분석은 각 문서의 대시보드에서 볼 수 있습니다.',
              en: 'This page is CloudBTL’s internal product analytics. To see how your own documents are read, open each document’s dashboard.',
            })}
          </p>
        </section>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <section className="analytics">
          <h1>{t({ ko: '분석을 사용할 수 없습니다', en: 'Analytics unavailable' })}</h1>
          <p>{error}</p>
        </section>
      </Shell>
    );
  }

  return (
    <Shell>
      <section className="analytics">
        <header>
          <h1>{t({ ko: '제품 분석', en: 'Product analytics' })}</h1>
          <label>
            {t({ ko: '기간', en: 'Range' })}
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
              <option value={7}>{t({ ko: '7일', en: '7 days' })}</option>
              <option value={14}>{t({ ko: '14일', en: '14 days' })}</option>
              <option value={30}>{t({ ko: '30일', en: '30 days' })}</option>
              <option value={90}>{t({ ko: '90일', en: '90 days' })}</option>
            </select>
          </label>
        </header>

        {!data ? (
          <p className="muted">{t({ ko: '불러오는 중…', en: 'Loading…' })}</p>
        ) : (
          <>
            <dl className="metric-grid">
              <Metric label={t({ ko: '방문자', en: 'Visitors' })} value={data.metrics.visitors} />
              <Metric label={t({ ko: '세션', en: 'Sessions' })} value={data.metrics.sessions} />
              <Metric label={t({ ko: '식별된 사용자', en: 'Identified' })} value={data.metrics.identifiedUsers} />
              <Metric label={t({ ko: '업로드', en: 'Uploads' })} value={data.metrics.uploads} />
              <Metric label={t({ ko: '공유 링크', en: 'Share links' })} value={data.metrics.shareLinks} />
              <Metric label={t({ ko: '페이지 조회수', en: 'Page views' })} value={data.metrics.pageViews} />
            </dl>

            <h2>{t({ ko: '퍼널', en: 'Funnel' })}</h2>
            <table className="table">
              <thead>
                <tr>
                  <th>{t({ ko: '단계', en: 'Step' })}</th>
                  <th>{t({ ko: '수', en: 'Count' })}</th>
                </tr>
              </thead>
              <tbody>
                {data.funnel.map((row) => (
                  <tr key={row.step}>
                    <td>{row.step}</td>
                    <td>{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h2>{t({ ko: '상위 경로', en: 'Top paths' })}</h2>
            <table className="table">
              <thead>
                <tr>
                  <th>{t({ ko: '경로', en: 'Path' })}</th>
                  <th>{t({ ko: '열람', en: 'Views' })}</th>
                  <th>{t({ ko: '방문자', en: 'Visitors' })}</th>
                  <th>{t({ ko: '클릭', en: 'Clicks' })}</th>
                  <th>{t({ ko: '이탈', en: 'Exits' })}</th>
                </tr>
              </thead>
              <tbody>
                {data.paths.map((row) => (
                  <tr key={row.path}>
                    <td>{row.path}</td>
                    <td>{row.views}</td>
                    <td>{row.visitors}</td>
                    <td>{row.clicks}</td>
                    <td>{row.exits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>
    </Shell>
  );
}

function Metric({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div className="metric">
      <dt className="metric-label">{label}</dt>
      <dd className="metric-value">{value}</dd>
    </div>
  );
}
